import { skipToken } from '@reduxjs/toolkit/query'

import {
  IApiSuccessWrapper,
  IFilterProfile,
  IFilterProfileLite,
  INetwork
} from '$/types'
import { useAppSelector } from 'app/hooks'
import { apiSlice } from 'app/services/api'
import { selectSystemScopeIdContainer } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import { IContinuousRouteRequestParams } from 'app/layout/Navbar/Filters/types'

export const systemScopeFilterProfilesApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['SSFilterProfile']
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // NOTE: GetAll returns "lite" version of profile!

      getSystemScopeFilterProfiles: build.query<IFilterProfileLite[], void>({
        query: () => '/systemScopes',
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IFilterProfileLite[]
        },
        providesTags: (res) => {
          const tags = [{ type: 'SSFilterProfile' as const, id: 'LIST' }]

          if (res != null) {
            tags.push(
              ...res.map((state) => ({
                type: 'SSFilterProfile' as const,
                id: state.id.toString()
              }))
            )
          }

          return tags
        }
      }),
      getSystemScopeFilterProfile: build.query<IFilterProfile, string>({
        query: (id) => `/systemScopes/${id}`,
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IFilterProfile
        },
        providesTags: (_res, _err, id) => [{ type: 'SSFilterProfile', id }]
      }),
      getContinuousRoutesFilterProfile: build.query<
        INetwork[],
        IContinuousRouteRequestParams | void
      >({
        query: (continuousRoutesParams) => ({
          url: '/gassystems/continuousRoute',
          method: 'POST',
          body: continuousRoutesParams ?? {
            operatorCodes: [] as string[],
            stateCodes: [] as string[],
            regionCodes: [] as string[],
            customQuery: '',
            customQueryString: ''
          }
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as INetwork[]
        }
      }),
      addSystemScopeFilterProfile: build.mutation<
        IFilterProfile,
        Partial<IFilterProfile>
      >({
        query: (state) => ({
          url: '/systemScopes',
          method: 'POST',
          body: state
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          // REVIEW: Does it respond with the full filter profile?
          return res.result as IFilterProfile
        },
        invalidatesTags: [{ type: 'SSFilterProfile', id: 'LIST' }]
      }),
      /**
       * TODO: [FE DEBT] Wire up system scope modal to make use of this call.
       *       Not all requests are wired up to use this and therefore
       *       cache invalidation doesn't apply properly.
       */
      updateSystemScopeFilterProfile: build.mutation<
        IFilterProfile,
        Partial<IFilterProfile>
      >({
        query: (state) => ({
          url: '/systemScopes',
          method: 'PUT',
          body: state
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IFilterProfile
        },
        invalidatesTags: (state) => [
          { type: 'SSFilterProfile', id: 'LIST' },
          { type: 'SSFilterProfile', id: state?.id }
        ]
      }),
      deleteSystemScopeFilterProfile: build.mutation<IFilterProfile, string>({
        query: (id) => ({
          url: `/systemScopes/${id}`,
          method: 'DELETE'
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IFilterProfile
        },
        invalidatesTags: (state) => [
          { type: 'SSFilterProfile', id: 'LIST' },
          { type: 'SSFilterProfile', id: state?.id }
        ]
      })
    })
  })

export const {
  useGetSystemScopeFilterProfilesQuery,
  useGetSystemScopeFilterProfileQuery,
  useLazyGetSystemScopeFilterProfileQuery,
  useAddSystemScopeFilterProfileMutation,
  useLazyGetContinuousRoutesFilterProfileQuery,
  useUpdateSystemScopeFilterProfileMutation,
  useDeleteSystemScopeFilterProfileMutation
} = systemScopeFilterProfilesApi

export const useGetActiveSystemScopeFilterProfileQuery = () => {
  const activeSystemScopeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  return useGetSystemScopeFilterProfileQuery(
    activeSystemScopeFilterProfileIdContainer.id?.toString() ?? skipToken,
    {
      selectFromResult: ({ currentData }) => ({
        systemScopeFilterProfile: currentData
      })
    }
  )
}
